/**
 * Design tokens — the single source of truth for the VibeWell design system.
 *
 * The actual runtime colors are applied as CSS custom properties in
 * `index.css` (so Tailwind + shadcn read them via `hsl(var(--token))`), but
 * these TypeScript tokens document the palette and expose it to JS/TS code
 * (charts, canvas, framer-motion) without hard-coding hex values in components.
 *
 * Breakpoints map the required device tiers:
 *   mobile → base · tablet → md · laptop → lg · desktop → xl · ultrawide → 3xl
 */

export const palette = {
  primary: "#0F766E", // teal 700
  primaryHover: "#0D6963",
  secondary: "#1E293B", // slate 800
  accent: "#06B6D4", // cyan 500
  background: "#F8FAFC", // slate 50
  surface: "#FFFFFF",
  ink: "#0F172A", // slate 900 — primary text
  muted: "#64748B", // slate 500 — secondary text
  border: "#E2E8F0", // slate 200
  success: "#10B981",
  warning: "#F59E0B",
  danger: "#EF4444",
} as const;

/** Spacing scale (rem). Mirrors an 8pt-friendly rhythm. */
export const spacing = {
  xs: "0.5rem",
  sm: "0.75rem",
  md: "1rem",
  lg: "1.5rem",
  xl: "2rem",
  "2xl": "3rem",
  "3xl": "4rem",
  "4xl": "6rem",
  "5xl": "8rem",
  section: "clamp(5rem, 10vw, 9rem)",
} as const;

/** Corner radii (16–20px range per brand). */
export const radii = {
  sm: "0.5rem",
  md: "0.75rem",
  lg: "1rem", // 16px — default card
  xl: "1.25rem", // 20px
  "2xl": "1.75rem",
  full: "9999px",
} as const;

/** Soft, layered shadows. */
export const shadows = {
  xs: "0 1px 2px rgba(15, 23, 42, 0.04)",
  sm: "0 2px 8px rgba(15, 23, 42, 0.05)",
  md: "0 8px 24px rgba(15, 23, 42, 0.06)",
  lg: "0 18px 48px rgba(15, 23, 42, 0.08)",
  glow: "0 24px 64px rgba(15, 118, 110, 0.18)",
} as const;

/** Responsive breakpoints in pixels. */
export const breakpoints = {
  mobile: 0,
  tablet: 768,
  laptop: 1024,
  desktop: 1280,
  ultrawide: 1920,
} as const;

export type Breakpoint = keyof typeof breakpoints;

/** Media-query strings for `useMediaQuery`. */
export const mediaQuery = {
  tablet: `(min-width: ${breakpoints.tablet}px)`,
  laptop: `(min-width: ${breakpoints.laptop}px)`,
  desktop: `(min-width: ${breakpoints.desktop}px)`,
  ultrawide: `(min-width: ${breakpoints.ultrawide}px)`,
  reducedMotion: "(prefers-reduced-motion: reduce)",
} as const;

export const tokens = {
  palette,
  spacing,
  radii,
  shadows,
  breakpoints,
  mediaQuery,
} as const;

export type Tokens = typeof tokens;
